import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import { DepartmentCreateRequest, DepartmentResponse, DepartmentListResponse, DepartmentLevelListResponse } from '../types';

/**
 * Department API Service
 */
export const departmentApi = {
    /**
     * Create a new department
     */
    createDepartment: async (schoolSlug: string, data: DepartmentCreateRequest): Promise<DepartmentResponse> => {
        try {
            const response = await apiClient.post<DepartmentResponse>(API_ENDPOINTS.DEPARTMENTS.CREATE(schoolSlug), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to create department'
            };
        }
    },

    /**
     * Update a department
     */
    updateDepartment: async (schoolSlug: string, departmentId: string, data: Partial<DepartmentCreateRequest>): Promise<DepartmentResponse> => {
        try {
            const response = await apiClient.patch<DepartmentResponse>(API_ENDPOINTS.DEPARTMENTS.UPDATE(schoolSlug, departmentId), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update department'
            };
        }
    },

    /**
     * Get all departments for a school
     */
    getDepartments: async (schoolSlug: string, params?: { page?: number; page_size?: number }): Promise<DepartmentListResponse> => {
        try {
            const response = await apiClient.get<DepartmentListResponse>(API_ENDPOINTS.DEPARTMENTS.LIST(schoolSlug), { params });
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch departments'
            };
        }
    },

    /**
     * Get levels for a specific department
     */
    getDepartmentLevels: async (schoolSlug: string, departmentId: string): Promise<DepartmentLevelListResponse> => {
        try {
            const response = await apiClient.get<DepartmentLevelListResponse>(API_ENDPOINTS.DEPARTMENTS.LEVELS(schoolSlug, departmentId));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch department levels'
            };
        }
    },

    /**
     * Create a new sub-department
     */
    createSubDepartment: async (schoolSlug: string, data: { name: string, department_id: string }): Promise<any> => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.SUB_DEPARTMENTS.CREATE(schoolSlug), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to create sub-department'
            };
        }
    },

    /**
     * Get sub-departments for a department
     */
    getSubDepartments: async (schoolSlug: string, departmentId: string): Promise<any> => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.SUB_DEPARTMENTS.LIST(schoolSlug, departmentId));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch sub-departments'
            };
        }
    },

    /**
     * Create a workflow step
     */
    createWorkflowStep: async (schoolSlug: string, data: { name: string, description: string, workflow_id: string }): Promise<any> => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.WORKFLOWS_NEW.STEPS.CREATE(schoolSlug), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to create workflow step'
            };
        }
    },

    /**
     * Create a workflow level
     */
    createWorkflowLevel: async (schoolSlug: string, data: { name: string, description: string, assigned_employee_id: string, can_read: boolean, can_write: boolean, can_resolve: boolean, can_escalate: boolean, can_forward: boolean, step_id: string }): Promise<any> => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.WORKFLOWS_NEW.LEVELS.CREATE(schoolSlug), data);
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to create workflow level'
            };
        }
    },

    /**
     * Get workflow steps for a department type
     */
    getWorkflowSteps: async (schoolSlug: string, workflowId: string): Promise<any> => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.WORKFLOWS_NEW.STEPS.LIST(schoolSlug, workflowId));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch workflow steps'
            };
        }
    },

    /**
     * Delete a department
     */
    deleteDepartment: async (schoolSlug: string, departmentId: string): Promise<any> => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.DEPARTMENTS.DELETE(schoolSlug, departmentId));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete department'
            };
        }
    },

    /**
     * Delete a sub-department
     */
    deleteSubDepartment: async (schoolSlug: string, typeId: string): Promise<any> => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.SUB_DEPARTMENTS.DELETE(schoolSlug, typeId));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete sub-department'
            };
        }
    },

    /**
     * Delete a workflow step
     */
    deleteWorkflowStep: async (schoolSlug: string, stepId: string): Promise<any> => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.WORKFLOWS_NEW.STEPS.DELETE(schoolSlug, stepId));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete workflow step'
            };
        }
    },

    /**
     * Delete a workflow level
     */
    deleteWorkflowLevel: async (schoolSlug: string, levelId: string): Promise<any> => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.WORKFLOWS_NEW.LEVELS.DELETE(schoolSlug, levelId));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete workflow level'
            };
        }
    },

    /**
     * Download import template
     */
    downloadTemplate: async (schoolSlug: string, type: string): Promise<any> => {
        try {
            const response = await apiClient.get(
                API_ENDPOINTS.IMPORT_EXPORT.DOWNLOAD_TEMPLATE(schoolSlug, type),
                { responseType: 'blob' }
            );
            return {
                success: true,
                data: response.data,
                headers: response.headers
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to download template'
            };
        }
    },

    /**
     * Import departments/employees from file
     */
    importFile: async (schoolSlug: string, type: string, file: File): Promise<any> => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await apiClient.post(
                API_ENDPOINTS.IMPORT_EXPORT.IMPORT_FILE(schoolSlug, type),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to import file'
            };
        }
    }
};
